<template>
    <div class="chat-sidebar-wrapper">
        <SidebarChild
            :default-open="true"
            :title-enabled="true"
            :selected-item="getSelectedItem()"
            :tabs="getMenuTabs()"
            :items="getMenuItems()"
            :focusActive="true"
            :enable-non-grouped-draggable="true"
            @selected="selectedItem"
            @contextmenu="handleContextMenu"
            @dragEnd="handleDragEnd"
            className="chat-menu"
        >
            <template #title>
                <div class="sidebar-title-text">{{ $t("chat.inbox") }}</div>
                <div class="sidebar-title-actions">
                    <Tooltip :title="$t('chat.sidebar.tabScreen')">
                        <div class="sidebar-title-action" @click="openTabScreen">
                            <div class="material-icons">backup_table</div>
                        </div>
                    </Tooltip>
                    <Tooltip :title="$t('chat.sidebar.search')">
                        <div class="sidebar-title-action" @click="search">
                            <div class="material-icons">search</div>
                        </div>
                    </Tooltip>
                </div>
            </template>

            <template #Views-empty>
                <div class="empty-group-item" @click="addNewFilter">
                    <div class="empty-group-item-icon">
                        <div class="material-icons">add_circle_outline</div>
                    </div>
                    <div class="empty-group-item-content">
                        <div class="empty-group-item-text">{{ $t('chat.sidebar.addNewFilter') }}</div>
                    </div>
                </div>
            </template>
        </SidebarChild>

        <!-- Context Menu -->
        <div
            v-if="contextMenuVisible"
            class="inbox-context-menu"
            :style="{
                left: `${contextMenuX}px`,
                top: `${contextMenuY}px`,
            }"
            @click.stop
        >
            <div class="context-menu-item" @click="handleEdit">
                <i class="material-icons">edit</i>
                <span>{{ $t('chat.sidebar.edit') }}</span>
            </div>
            <div class="context-menu-item" @click="handleOpenInTable">
                <i class="material-icons">table_chart</i>
                <span>{{ $t('chat.sidebar.showInTable') }}</span>
            </div>
            <div class="context-menu-item" @click="handleOpenInKanban">
                <i class="material-icons">view_kanban</i>
                <span>{{ $t('chat.sidebar.showInKanban') }}</span>
            </div>

            <template v-if="isCustomFilter">
                <div class="context-menu-divider"></div>
                <div class="context-menu-item" @click="handleRename">
                    <i class="material-icons">drive_file_rename_outline</i>
                    <span>{{ $t('chat.sidebar.rename') }}</span>
                </div>
                <div class="context-menu-item" @click="handleToggleVisibility">
                    <i class="material-icons">{{ $_.get(selectedFilter, 'visibility') === 'private' ? 'lock_open' : 'lock' }}</i>
                    <span>{{ $_.get(selectedFilter, 'visibility') === 'private' ? $t('chat.sidebar.makePublic') : $t('chat.sidebar.makePrivate') }}</span>
                </div>
            </template>

            <div class="context-menu-divider"></div>
            <div class="context-menu-item" @click="handleMoveToTop">
                <i class="material-icons">vertical_align_top</i>
                <span>{{ $t('chat.sidebar.moveToTop') }}</span>
            </div>
            <div class="context-menu-item" @click="handleMoveToBottom">
                <i class="material-icons">vertical_align_bottom</i>
                <span>{{ $t('chat.sidebar.moveToBottom') }}</span>
            </div>

            <template v-if="isCustomFilter">
                <div class="context-menu-divider"></div>
                <div class="context-menu-item danger" @click="handleDelete">
                    <i class="material-icons">delete</i>
                    <span>{{ $t('chat.sidebar.delete') }}</span>
                </div>
            </template>
        </div>

        <!-- Rename Modal -->
        <a-modal
            v-model="renameModalVisible"
            :title="$t('chat.sidebar.renameFilterTitle')"
            @ok="handleRenameSubmit"
            @cancel="renameModalVisible = false"
        >
            <a-input
                v-model="renameValue"
                :placeholder="$t('chat.sidebar.filterNamePlaceholder')"
                @pressEnter="handleRenameSubmit"
            />
        </a-modal>
    </div>
</template>
<script>
import SidebarChild from "../../../../components/Sidebar/SidebarChild.vue";
import { mapGetters, mapState, mapActions } from "vuex";
import { Tooltip, Modal } from "ant-design-vue";
import _ from "lodash";
import SYSTEM_PUBSUB from "../../../../utils/systemPubSub";
import { updateFilterMultipleKey, deleteFilter } from "../../../../api/favFilters";

export default {
    name: "ChatSidebar",
    components: {
        SidebarChild,
        Tooltip,
    },
    props: {
        value: {
            type: [String, Number],
            default: null,
        },
    },
    computed: {
        ...mapState({
            customerDomains: (state) => state.visitor.customerDomains,
            liveVisitorCount: (state) => state.visitor.liveVisitorCount,
            chatTotal: (state) => state.chat.stats.total,
            unreadMessages: (state) => state.team.unreadMessages.filter((chat) => chat.unreadCount > 0).length,
            chatbotLive: (state) => state.chat.stats.chatbot,
            websiteTrackingUrl: (state) => state.setting.settings?.websiteTrackingUrl,
            whatsappChannelExists: (state) =>
                state.channel.channels.find((channel) => channel.type.includes("whatsapp")),
            messengerChannelExists: (state) =>
                state.channel.channels.find((channel) => channel.type.includes("messenger")),
            instagramChannelExists: (state) =>
                state.channel.channels.find((channel) => channel.type.includes("instagram")),
            linkedinChannelExists: (state) =>
                state.channel.channels.find((channel) => channel.type.includes("linkedin-unipile")),
            telegramChannelExists: (state) =>
                state.channel.channels.find((channel) => channel.type.includes("telegram-unipile")),
            metaLeadsChannelExists: (state) =>
                state.channel.channels.find((channel) => channel.type.includes("meta-lead")),
            channels: (state) => state.channel.channels,
            chatFilters: (state) => state.contact.filters,
            route: (state) => state.app.route,
            settings: (state) => state.setting.settings,
            currentUser: (state) => state.auth.user,
            activeView: (state) => state.contact.activeView,
        }),
        hasChannelWithChatbotStory() {
            return this.channels.some((channel) => {
                return channel.forward && channel.forward.story && channel.forward.story !== null;
            });
        },
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
    },
    data: () => ({
        MenuItems: [],
        changeCount: 0,
        customerDomainsFetched: false,
        contextMenuVisible: false,
        contextMenuX: 0,
        contextMenuY: 0,
        selectedFilter: null,
        isCustomFilter: false,
        renameModalVisible: false,
        renameValue: "",
        renamingFilterId: null,
    }),
    mounted() {
        console.log("ChatSidebar mounted");
        this.MenuItems = this.getMenuItems();
        document.addEventListener("click", this.closeContextMenu);
        this.getCustomerDomainsIfNeed().then(() => {
            this.customerDomainsFetched = true;
            this.MenuItems = this.getMenuItems();
        });
    },
    beforeDestroy() {
        document.removeEventListener("click", this.closeContextMenu);
    },
    created() {
        this.MenuItems = this.getMenuItems();
    },
    methods: {
        ...mapActions({
            syncFilters: "favFilters/syncFilters",
            setActiveView: "contact/setActiveView",
            getCustomerDomainsIfNeed: "visitor/getCustomerDomainsIfNeed",
        }),
        isSiteIntegrated() {
            return this.customerDomainsFetched && this.customerDomains.length;
        },
        openTabScreen() {
            this.$router.push({
                path: "/chat",
                query: {
                    tab: "inbox",
                    view: "tab",
                },
            });
        },
        getSelectedItem() {
            if (this.isInbox(this.value)) {
                const inbox = this.$route.query.inbox;
                return `inbox-${inbox}`;
            }
            return this.value;
        },
        isInbox(key) {
            return key === "inbox" || key.startsWith("inbox-");
        },
        selectedItem({ key }) {
            if (this.isInbox(key)) {
                const inboxKey = key.split("-")[1];
                this.$emit("input", "inbox");
                this.$router.push(`/chat?tab=inbox&inbox=${inboxKey}&view=${this.route.query.view || "chat"}`);
                return;
            }
            this.$emit("input", key);
        },
        search() {
            this.$router.replace({
                query: {
                    tab: "inbox",
                    inbox: "allContacts",
                    view: "filter",
                },
            });
        },
        addNewFilter() {
            this.$router.push("/chat?tab=inbox&inbox=allContacts&view=filter");
        },
        async handleDragEnd({ groupName, items }) {
            const isCustomFiltersGroup = groupName === this.$t("chat.groups.customFilters");
            const isSystemFiltersGroup = groupName === null;

            if (isCustomFiltersGroup) {
                const updates = items.map((item, index) => {
                    const filter = item.filterData;
                    if (filter && filter._id) {
                        return updateFilterMultipleKey(filter._id, {
                            position: index,
                        }).catch((error) => {
                            console.error("Pozisyon güncellenirken hata:", error);
                            return null;
                        });
                    }
                    return Promise.resolve();
                });

                try {
                    await Promise.all(updates);
                    await this.$store.dispatch("contact/getContactListFilters");
                } catch (error) {
                    console.error("Pozisyon güncellenirken hata oluştu:", error);
                    this.$message.error(this.$t('chat.sidebar.positionUpdateError'));
                }
            } else if (isSystemFiltersGroup) {
                const systemFilterKeys = items
                    .filter((item) => item.filterData && item.filterData.type === "system")
                    .map((item) => item.filterData.key);

                if (systemFilterKeys.length > 0) {
                    this.saveSystemFilterOrder(systemFilterKeys);
                }
            }
        },
        handleContextMenu(event, item) {
            const filter = this.chatFilters.find((f) => `inbox-${f.key}` === item.key);
            if (!filter) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();

            this.selectedFilter = filter;
            this.isCustomFilter = filter.type === "custom";

            const menuWidth = 220;
            const menuHeight = this.isCustomFilter ? 320 : 180;

            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;

            let posX = event.pageX;
            if (posX + menuWidth > windowWidth) {
                posX = event.pageX - menuWidth;
            }

            let posY = event.pageY;
            if (posY + menuHeight > windowHeight) {
                posY = event.pageY - menuHeight;
            }

            posX = Math.max(10, posX);
            posY = Math.max(10, posY);

            this.contextMenuX = posX;
            this.contextMenuY = posY;
            this.contextMenuVisible = true;
        },
        closeContextMenu() {
            this.contextMenuVisible = false;
            this.selectedFilter = null;
            this.isCustomFilter = false;
        },
        handleEdit() {
            if (!this.selectedFilter) return;
            const filterKey = this.selectedFilter.key;
            this.closeContextMenu();
            this.$router.push(`/chat?tab=inbox&inbox=${filterKey}&view=filter`);
        },
        handleRename() {
            if (!this.selectedFilter) return;
            this.renameValue = this.selectedFilter.name;
            this.renamingFilterId = this.selectedFilter._id;
            this.renameModalVisible = true;
            this.closeContextMenu();
        },
        async handleRenameSubmit() {
            if (!this.renameValue || !this.renameValue.trim()) {
                this.$message.error(this.$t('chat.sidebar.filterNameEmpty'));
                return;
            }
            if (!this.renamingFilterId) return;
            try {
                await updateFilterMultipleKey(this.renamingFilterId, {
                    name: this.renameValue.trim(),
                });
                this.$message.success(this.$t('chat.sidebar.filterNameUpdated'));
                this.renameModalVisible = false;
                this.renameValue = "";
                this.renamingFilterId = null;
                await this.$store.dispatch("contact/getContactListFilters");
            } catch (error) {
                console.error( error);
                this.$message.error(this.$t('chat.sidebar.filterNameUpdateError'));
            }
        },
        async handleToggleVisibility() {
            if (!this.selectedFilter) return;
            const filterId = this.selectedFilter._id;
            const newVisibility = this.selectedFilter.visibility === "private" ? "public" : "private";
            this.closeContextMenu();
            try {
                await updateFilterMultipleKey(filterId, {
                    visibility: newVisibility,
                });
                this.$message.success(newVisibility === "private" ? this.$t('chat.sidebar.filterMadePrivate') : this.$t('chat.sidebar.filterMadePublic'));
                await this.$store.dispatch("contact/getContactListFilters");
            } catch (error) {
                this.$message.error(this.$t('chat.sidebar.filterVisibilityUpdateError'));
            }
        },
        handleOpenInTable() {
            if (!this.selectedFilter) return;
            const filterKey = this.selectedFilter.key;
            this.closeContextMenu();
            this.$router.push(`/chat?tab=inbox&inbox=${filterKey}&view=table`);
        },
        handleOpenInKanban() {
            if (!this.selectedFilter) return;
            const filterKey = this.selectedFilter.key;
            this.closeContextMenu();
            this.$router.push(`/chat?tab=inbox&inbox=${filterKey}&view=kanban`);
        },
        async handleMoveToTop() {
            if (!this.selectedFilter) return;

            const filter = this.selectedFilter;
            this.closeContextMenu();

            if (filter.type === "custom") {
                const customFilters = this.chatFilters.filter((f) => f.type === "custom");
                const minPosition = Math.min(...customFilters.map((f) => f.position || 0));
                const newPosition = minPosition - 1;

                try {
                    await updateFilterMultipleKey(filter._id, { position: newPosition });
                    this.$message.success(this.$t('chat.sidebar.filterMovedToTop'));
                    await this.$store.dispatch("contact/getContactListFilters");
                } catch (error) {
                    console.error(error);
                    this.$message.error(this.$t('chat.sidebar.filterMoveError'));
                }
            } else {
                const systemFilterOrder = this.getSystemFilterOrder();
                const currentIndex = systemFilterOrder.indexOf(filter.key);

                if (currentIndex > 0) {
                    systemFilterOrder.splice(currentIndex, 1);
                    systemFilterOrder.unshift(filter.key);
                    this.saveSystemFilterOrder(systemFilterOrder);
                    this.$message.success(this.$t('chat.sidebar.filterMovedToTop'));
                }
            }
        },
        async handleMoveToBottom() {
            if (!this.selectedFilter) return;

            const filter = this.selectedFilter;
            this.closeContextMenu();

            if (filter.type === "custom") {
                const customFilters = this.chatFilters.filter((f) => f.type === "custom");
                const maxPosition = Math.max(...customFilters.map((f) => f.position || 0));
                const newPosition = maxPosition + 1;

                try {
                    await updateFilterMultipleKey(filter._id, { position: newPosition });
                    this.$message.success(this.$t('chat.sidebar.filterMovedToBottom'));
                    await this.$store.dispatch("contact/getContactListFilters");
                } catch (error) {
                    console.error(error);
                    this.$message.error(this.$t('chat.sidebar.filterMoveError'));
                }
            } else {
                const systemFilterOrder = this.getSystemFilterOrder();
                const currentIndex = systemFilterOrder.indexOf(filter.key);

                if (currentIndex !== -1 && currentIndex < systemFilterOrder.length - 1) {
                    systemFilterOrder.splice(currentIndex, 1);
                    systemFilterOrder.push(filter.key);
                    this.saveSystemFilterOrder(systemFilterOrder);
                    this.$message.success(this.$t('chat.sidebar.filterMovedToBottom'));
                }
            }
        },
        getSystemFilterOrder() {
            try {
                const stored = localStorage.getItem("systemFilterOrder");
                if (stored) {
                    return JSON.parse(stored);
                }
            } catch (error) {
                console.error("System filter order parse error:", error);
            }

            const systemFilters = this.chatFilters.filter((f) => f.type === "system");
            return systemFilters.map((f) => f.key);
        },
        saveSystemFilterOrder(order) {
            try {
                localStorage.setItem("systemFilterOrder", JSON.stringify(order));
                this.$store.dispatch("contact/getContactListFilters");
            } catch (error) {
                console.error("System filter order save error:", error);
            }
        },
        async handleDelete() {
            const filter = this.selectedFilter;

            const canDelete = this.checkPermission("root") || filter.user === this.currentUser._id;

            if (!canDelete && filter.visibility === "public") {
                this.$message.error(this.$t('chat.sidebar.onlyCreatorOrRootCanDeletePublic'));
                this.closeContextMenu();
                return;
            }

            this.closeContextMenu();

            Modal.confirm({
                title: this.$t('chat.sidebar.deleteFilterTitle'),
                content: this.$t('chat.sidebar.deleteFilterConfirm', { name: filter.name }),
                okText: this.$t('chat.sidebar.deleteFilterOk'),
                okType: "danger",
                cancelText: this.$t('chat.sidebar.deleteFilterCancel'),
                onOk: async () => {
                    try {
                        await deleteFilter(filter._id);
                        this.$message.success(this.$t('chat.sidebar.filterDeleted'));

                        if (this.$route.query.inbox === filter.key) {
                            this.$router.push(`/chat?tab=inbox&inbox=allContacts&view=${this.activeView || 'chat'}`);
                        }

                        await this.$store.dispatch("contact/getContactListFilters");
                    } catch (error) {
                        this.$message.error(this.$t('chat.sidebar.filterDeleteError'));
                    }
                },
            });
        },
        getMenuTabs() {
            return [
                {
                    label: this.$t("chat.groups.customFilters"),
                    draggable: true,
                },
                {
                    label: this.$t("chat.groups.team"),
                    draggable: false,
                },
                {
                    label: this.$t("chat.groups.system"),
                    draggable: false,
                },
            ]
        },
        getMenuItems() {
            const translate = (key, name) => {
                const text = this.$t(`chat.filters.${key}`);
                return text === `chat.filters.${key}` ? name : text;
            };

            const sortByPosition = (filters) => {
                return filters.sort((a, b) => {
                    if (a.position === b.position) {
                        return new Date(a.createdAt) - new Date(b.createdAt);
                    }
                    return (a.position || 0) - (b.position || 0);
                });
            };

            const sortSystemFilters = (filters) => {
                const systemFilterOrder = this.getSystemFilterOrder();
                const orderedFilters = [];
                const unorderedFilters = [];

                filters.forEach((filter) => {
                    const orderIndex = systemFilterOrder.indexOf(filter.key);
                    if (orderIndex !== -1) {
                        orderedFilters[orderIndex] = filter;
                    } else {
                        unorderedFilters.push(filter);
                    }
                });

                return orderedFilters.filter(Boolean).concat(unorderedFilters);
            };

            const systemFilters = this.chatFilters.filter((filter) => filter.type === "system");
            const sortedSystemFilters = sortSystemFilters(systemFilters);

            return [
                ...sortedSystemFilters.map((filter) => {
                    const isChatbotFilter = filter.key === "chatbot" || filter.key?.includes("chatbot");
                    return {
                        enabled: isChatbotFilter ? this.hasChannelWithChatbotStory : true,
                        key: `inbox-${filter.key}`,
                        isSelected: this.isInbox(this.value) && this.value.split("-")[1] === filter.key,
                        label: translate(filter.key, filter.name),
                        badge: !filter.viewed && filter.count > 0,
                        icon: {
                            type: "material-icons",
                            name: filter.icon || "filter_list",
                        },
                        dataKey: filter.key,
                        showSearchButton: true,
                        filterData: filter,
                        contextMenuEnabled: true,
                        onContextMenu: (event, item) => {
                            this.handleContextMenu(event, item);
                        },
                        clickCallback: () => {
                            this.$router.push(
                                `/chat?tab=inbox&inbox=${filter.key}&view=${this.activeView || 'chat'}`
                            );
                        },

                        dblclickCallback: () => {
                            this.$router.push(
                                `/chat?tab=inbox&inbox=${filter.key}&view=list`
                            );
                        },
                        searchCallback: () => {
                            this.$router.push(`/chat?tab=inbox&inbox=${filter.key}&view=filter`);
                        },
                    };
                }),
                ...sortByPosition(this.chatFilters.filter((filter) => filter.type === "custom"))
                    .map((filter) => ({
                        group: this.$t("chat.groups.customFilters"),
                        enabled: true,
                        key: `inbox-${filter.key}`,
                        isSelected: this.isInbox(this.value) && this.value.split("-")[1] === filter.key,
                        label: filter.name,
                        badge: !filter.viewed && filter.count > 0,
                        icon: {
                            type: "material-icons",
                            name: filter.icon || "filter_list",
                        },
                        dataKey: filter.key,
                        showSearchButton: true,
                        isPrivate: filter.visibility === "private",
                        filterData: filter,
                        contextMenuEnabled: true,
                        onContextMenu: (event, item) => {
                            this.handleContextMenu(event, item);
                        },
                        clickCallback: () => {
                            this.$router.push(
                                `/chat?tab=inbox&inbox=${filter.key}&view=${this.activeView || 'chat'}`
                            );
                        },
                        dblclickCallback: () => {
                            this.$router.push(
                                `/chat?tab=inbox&inbox=${filter.key}&view=list`
                            );
                        },
                        searchCallback: () => {
                            this.$router.push(`/chat?tab=inbox&inbox=${filter.key}&view=filter`);
                        },
                    })),
                {
                    group: this.$t("chat.groups.team"),
                    enabled: true,
                    key: "teamChat",
                    label: this.$t("message.teamChat"),
                    counter: this?.unreadMessages,
                    icon: {
                        type: "material-icons",
                        name: "group",
                    },
                    dataKey: "teamChat",
                    clickCallback: () => {
                        this.$router.push(`/chat?tab=teamChat`);
                    },
                },
                {
                    enabled: this.isSiteIntegrated(),
                    key: "siteVisitors",
                    label: this.$t("message.siteVisitors"),
                    counter: this?.liveVisitorCount,
                    icon: {
                        type: "material-icons",
                        name: "public",
                    },
                    dataKey: "siteVisitors",
                    clickCallback: () => {
                        this.$router.push(`/chat?tab=siteVisitors`);
                    },
                },
                {
                    enabled: true,
                    key: "conversationHistory",
                    label: this.$t("message.conversationHistory"),
                    icon: {
                        type: "material-icons",
                        name: "history",
                    },
                    dataKey: "conversationHistory",
                    clickCallback: () => {
                        this.$router.push(`/chat?tab=conversationHistory`);
                    },
                },
                {
                    group: this.$t("chat.groups.team"),
                    enabled: true,
                    key: "representative",
                    label: this.$t("message.representative"),
                    icon: {
                        type: "material-icons",
                        name: "engineering",
                    },
                    dataKey: "representative",
                    clickCallback: () => {
                        this.$router.push(`/chat?tab=representative`);
                    },
                },
                {
                    group: this.$t("chat.groups.system"),
                    enabled: true,
                    key: "activities",
                    label: this.$t("notification.activities"),
                    icon: {
                        type: "material-icons",
                        name: "rss_feed",
                    },
                    dataKey: "activities",
                    clickCallback: () => {
                        this.$router.push(`/chat?tab=activities`);
                    },
                },
                {
                    group: this.$t("chat.groups.system"),
                    enabled: !!this?.websiteTrackingUrl,
                    key: "websiteAnalytics",
                    label: this.$t('chat.sidebar.websiteAnalytics'),
                    icon: {
                        type: "material-icons",
                        name: "analytics",
                    },
                    dataKey: "websiteAnalytics",
                    clickCallback: () => {
                        this.$router.push(`/chat?tab=websiteAnalytics`);
                    },
                },
                {
                    group: this.$t("chat.groups.system"),
                    enabled: true,
                    key: "automations",
                    label: this.$t("automation.automations"),
                    icon: {
                        type: "component",
                        name: "AutomationIcon",
                        class: "myIcon",
                        style: { fill: "var(--color-icon-primary-default)" },
                    },
                    dataKey: "automations",
                    clickCallback: () => {
                        this.$router.push(`/chat?tab=automations`);
                    },
                },
            ];
        },
    },
    watch: {
        liveVisitorCount() {
            this.MenuItems = this.getMenuItems();
        },
        chatTotal() {
            this.MenuItems = this.getMenuItems();
        },
        unreadMessages() {
            this.MenuItems = this.getMenuItems();
        },
        chatbotLive() {
            this.MenuItems = this.getMenuItems();
        },
        chatFilters: {
            deep: true,
            handler() {
                this.MenuItems = this.getMenuItems();
            },
        },
        customerDomains() {
            this.MenuItems = this.getMenuItems();
        },
        channels: {
            deep: true,
            handler() {
                this.MenuItems = this.getMenuItems();
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.chat-sidebar-wrapper {
    position: relative;
    height: 100%;
}

.empty-group-item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    padding: 8px;
    margin: 2px 0;
    border-radius: 4px;
    cursor: pointer;
    user-select: none;
    border: 1px dashed var(--color-border-default);
    background: transparent;
    transition: all 0.2s ease;

    &:hover {
        background: var(--color-bg-neutral-hover);
        border-color: #5625cc;
        border-style: solid;

        .empty-group-item-icon .material-icons {
            color: #5625cc;
        }

        .empty-group-item-text {
            color: #5625cc;
        }
    }

    .empty-group-item-icon {
        width: 20px;
        height: 20px;
        font-size: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 8px;

        .material-icons {
            font-size: 20px;
            color: #3a3f48;
            transition: color 0.2s ease;
        }
    }

    .empty-group-item-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        overflow: hidden;
    }

    .empty-group-item-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: calc(100% - 60px);
        min-width: calc(100% - 60px);
        color: #3a3f48;
        font-size: 14px;
        line-height: 1.5;
        transition: color 0.2s ease;
    }
}

.inbox-context-menu {
    position: fixed;
    background: var(--color-bg-default);
    border: 1px solid var(--color-border-default);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 9999;
    min-width: 200px;
    max-width: 220px;
    padding: 4px;
    max-height: 90vh;
    overflow-y: auto;

    .context-menu-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        cursor: pointer;
        border-radius: 4px;
        transition: background 0.2s ease;
        font-size: 14px;
        color: var(--color-text-primary);

        &:hover {
            background: var(--color-bg-neutral-hover);
        }

        &.danger {
            color: #ff4d4f;

            &:hover {
                background: #fff1f0;
            }
        }

        i.material-icons {
            font-size: 18px;
            color: inherit;
        }
    }

    .context-menu-divider {
        height: 1px;
        background: var(--color-border-default);
        margin: 4px 0;
    }
}
</style>

<style lang="scss">
.chat-menu {
    .sidebar-menu-item-text{
        max-width:unset !important;
        min-width:unset !important;
    }
}
</style>
